import { ApplicationElement, html } from '@crossiety/lit-components';
import '@crossiety/lit-components/components/dropdown/element.js';
import '@crossiety/lit-components/components/icon/element.js';
import CommonActions from '@crossiety/lit-components/components/common_actions/element.js';

import '../notification/element.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * App header notifications dropdown
 *
 * @prop loadUrl - URL for fetching notifications
 * @prop markAllAsReadUrl - URL for marking all unread notifications as read
 * @prop notificationsPageUrl - On small screens we don't open dropdown, but redirect to this URL
 * @prop isLoading - Will be set automatically when fetching starts/finishes
 * @prop isError - Will be set automatically on fetch success/error
 *
 * @slot loader - Shown while fetching
 * @slot error - Shown when fetch is unsuccessful
 * @slot empty - Shown when fetch is successful, but no notifications are present
 *
 * @event notificationTopDropdown-markAllAsRead - Mark all as read action finished successfully
 */

export default class NotificationTopDropdown extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    loadUrl: { type: String },
    markAllAsReadUrl: { type: String },
    notificationsPageUrl: { type: String },
    isLoading: { type: Boolean },
    isError: { type: Boolean },
    unreadCount: { type: Number, state: true },
    items: { type: Array, state: true },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();

    this.items = [];
    /** @internal */
    this.commonActions = new CommonActions(this);
    this.unreadCount = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this);
    this.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this);
  }

  /** @internal */
  get unreadItems() {
    return this.items.filter(i => !i.isRead);
  }

  /** @internal */
  get uniqueItems() {
    return Array.from(
      new Map(this.unreadItems.map(item => [item.id, item])).values()
    );
  }

  /** @internal */
  get sortedUniqueItems() {
    return this.uniqueItems.sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
    );
  }

  /** @internal */
  get isSmallDevice() {
    return window.innerWidth < 992;
  }

  /** @internal */
  get isEmpty() {
    return !this.isLoading && !this.isError && !this.uniqueItems.length;
  }

  async handleMarkAllAsRead() {
    try {
      await this.commonActions.post(this.markAllAsReadUrl);
      this.items = [];
      this.unreadCount = 0;
      this.commonActions.displaySnackBarSuccess(
        this.t('snack.mark_all_as_read_success')
      );
      this.dispatchEvent(
        new Event('notificationTopDropdown-markAllAsRead', {
          bubbles: true,
          composed: true,
        })
      );
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  async load() {
    try {
      this.isLoading = true;

      const data = await (
        await this.commonActions.get(this.loadUrl, {
          headers: { Accept: 'application/json' },
        })
      ).json();

      this.items = data.notifications;
      this.unreadCount = data.total_unread;

      this.isError = false;
    } catch {
      this.isError = true;
    } finally {
      this.isLoading = false;
    }
  }

  handleEvent(e) {
    if (e.type === 'resize' && this.isSmallDevice !== this._wasSmallDevice) {
      this._wasSmallDevice = this.isSmallDevice;
      this.requestUpdate();
    }
  }

  renderNotificationList() {
    return this.sortedUniqueItems.map(notification =>
      this.renderNotification(notification)
    );
  }

  renderNotification(notification) {
    if (!notification) return null;

    const { id, createdAt, url, isRead, picture, pictureAlt, content } =
      notification;

    const modifiedHref = new URL(url, window.location.origin);
    if (!isRead) modifiedHref.searchParams.set('mark_as_read_notification', id);

    return html`
      <xs-notification
        data-id=${id}
        .createdAt=${createdAt}
        .url=${modifiedHref}
        ?isRead=${isRead}
        .picture=${picture}
        .pictureAlt=${pictureAlt || this.t('notification_image_alt')}>
        <span>${content}</span>
      </xs-notification>
    `;
  }

  renderTriggerIcon() {
    return html`
      <ef-icon name="bell" badgeCount=${this.unreadCount}></ef-icon>
    `;
  }

  renderHeader() {
    return html`
      <header>
        <button
          class="xs-btn xs-btn-sm btn-outline-secondary"
          @click=${this.handleMarkAllAsRead}>
          ${this.t('mark_all_as_read')}
        </button>
      </header>
    `;
  }

  renderFull() {
    return html`
      <ef-dropdown placement="bottom-end">
        <button slot="trigger" class="trigger">
          ${this.renderTriggerIcon()}
        </button>
        <div class="dropdown">
          ${this.unreadCount ? this.renderHeader() : null}

          <main>
            ${this.uniqueItems.length ? this.renderNotificationList() : null}
          </main>

          <slot name="empty" ?hidden=${!this.isEmpty}>
            ${this.t('empty')}
          </slot>
          <slot name="loader" ?hidden=${!this.isLoading}>
            ${this.t('loading')}
          </slot>
          <slot name="error" ?hidden=${!this.isError}>
            ${this.t('error')}
          </slot>

          <footer>
            <a
              class="xs-btn xs-btn-sm btn-outline-secondary"
              href=${this.notificationsPageUrl}>
              ${this.t('show_all')}
            </a>
          </footer>
        </div>
      </ef-dropdown>
    `;
  }

  renderOnlyIcon() {
    return html`
      <a
        href=${this.notificationsPageUrl}
        class="trigger"
        aria-label=${this.t('show_all')}>
        ${this.renderTriggerIcon()}
      </a>
    `;
  }

  render() {
    return html`
      ${this.isSmallDevice ? this.renderOnlyIcon() : this.renderFull()}
      <span class="sr-only" aria-live="polite" aria-atomic="true">
        ${this.t('unread_notifications', { unreadCount: this.unreadCount })}
      </span>
    `;
  }
}

NotificationTopDropdown.registerTagName('xs-notification-top-dropdown');

customElements.get('xs-notification-top-dropdown') ||
  customElements.define(
    'xs-notification-top-dropdown',
    NotificationTopDropdown
  );
